import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { AppFilterType, FilterTypeValue } from "@/components/filter/filter.types.ts";
import { useAppFilterApprovalRenders } from "@/components/filter/renders/filter-approval-renders.tsx";
import { useAppFilterEmployeeRenders } from "@/components/filter/renders/filter-employee-renders.tsx";
import { useAppFilterRecruitRenders } from "@/components/filter/renders/filter-recruit-renders.tsx";
import { useAppFilterTimeOffRenders } from "@/components/filter/renders/filter-time-off-renders.tsx";
import { FormParticipantStatusFilterRender } from "@/components/filter/renders/FormParticipantStatusFilterRender.tsx";
import { GoalOwnerFilterRender } from "@/components/filter/renders/GoalOwnerFilterRender.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFilterAbstract } from "@skailer-com/uikit/core";
import {
  FilterItem,
  FilterModal,
  FilterRenderContext,
  ModalProps,
  UseFilterActiveFilter,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";

interface DomainFilterRendererProps {
  item: FilterItem<FilterTypeValue>;
  context: FilterRenderContext;
  filters: FilterItem<FilterTypeValue>[];
}

const EmployeeDomainFilterRenderer = ({ item, context, filters }: DomainFilterRendererProps) => {
  const { control, errors, typedFilters } = context;
  const employeeRenders = useAppFilterEmployeeRenders({
    control,
    typedFilters,
    errors,
    filters,
  });

  switch (item.type) {
    case AppFilterType.employee:
      return employeeRenders.renderFilterEmployee(item);
    case AppFilterType.employeeDivision:
      return employeeRenders.renderFilterDivisions(item);
    case AppFilterType.employeeJobProfile:
      return employeeRenders.renderFilterJobProfiles(item);
    case AppFilterType.employeeJobRole:
      return employeeRenders.renderFilterJobRoles(item);
    case AppFilterType.employeeDepartment:
      return employeeRenders.renderFilterDepartments(item);
    case AppFilterType.employeeTeam:
      return employeeRenders.renderFilterTeams(item);
    case AppFilterType.employeeLocation:
      return employeeRenders.renderFilterLocations(item);
    case AppFilterType.employeeLifecycleStatus:
      return employeeRenders.renderFilterEmployeeLifecycle(item);
    case AppFilterType.employmentType:
      return employeeRenders.renderFilterEmploymentTypes(item);
    case AppFilterType.employmentContract:
      return employeeRenders.renderFilterEmploymentContracts(item);
    case AppFilterType.employmentLegalEntity:
      return employeeRenders.renderFilterLegalEntities(item);
    case AppFilterType.employeeHiredOn:
      return employeeRenders.renderFilterHiredOn(item);
    case AppFilterType.employeeBirthday:
      return employeeRenders.renderFilterBirthday(item);
    case AppFilterType.employeeTerminationDate:
      return employeeRenders.renderFilterTerminationDate(item);
    default:
      return null;
  }
};

const RecruitDomainFilterRenderer = ({ item, context, filters }: DomainFilterRendererProps) => {
  const { control, errors } = context;
  const recruitRenders = useAppFilterRecruitRenders({
    control,
    errors,
    filters,
  });

  switch (item.type) {
    case AppFilterType.jobs:
      return recruitRenders.renderFilterJobs(item);
    case AppFilterType.disqualificationReason:
      return recruitRenders.renderFilterDisqualificationReasons(item);
    case AppFilterType.jobCreationDate:
      return recruitRenders.renderFilterJobCreationDate();
    default:
      return null;
  }
};

const ApprovalDomainFilterRenderer = ({
  item,
  context,
}: Omit<DomainFilterRendererProps, "filters">) => {
  const { control, errors, typedFilters } = context;
  const approvalRenders = useAppFilterApprovalRenders({
    control,
    typedFilters,
    errors,
  });

  if (item.type === AppFilterType.approvalRequestStatus) {
    return approvalRenders.renderFilterApprovalStatus(item);
  }

  return null;
};

const TimeOffDomainFilterRenderer = ({ item, context, filters }: DomainFilterRendererProps) => {
  const { control, errors, typedFilters } = context;
  const timeOffRenders = useAppFilterTimeOffRenders({
    control,
    typedFilters,
    errors,
    filters,
  });

  if (item.type === AppFilterType.timeOffCategory) {
    return timeOffRenders.renderFilterTimeOffCategory(item);
  }

  return null;
};

const GoalDomainFilterRenderer = ({
  item,
  context,
}: Omit<DomainFilterRendererProps, "filters">) => {
  if (item.type !== AppFilterType.goalOwner) return null;

  return <GoalOwnerFilterRender item={item} control={context.control} errors={context.errors} />;
};

export interface AppFilterModalProps<T extends ApiFilterAbstract> extends ModalProps {
  filters: FilterItem<FilterTypeValue>[];
  currentFilter: UseFilterActiveFilter<T>;
  source: string;
}

export const AppFilterModal = <T extends ApiFilterAbstract>({
  filters,
  currentFilter,
  source,
  ...modalProps
}: AppFilterModalProps<T>) => {
  const { close } = useModals();
  const { id } = modalProps;

  const trackFilterEvent = (source: string) => {
    analyticsService.trackEvent(analyticEvents.reporting.filtered, {
      [analyticProperties.source]: source,
    });
  };

  const enhancedFilters: FilterItem<FilterTypeValue>[] = useMemo(() => {
    return filters.map((filter) => {
      if (Object.values(AppFilterType).includes(filter.type as AppFilterType)) {
        return {
          ...filter,
          render: (item: FilterItem<FilterTypeValue>, context: FilterRenderContext) =>
            renderDomainFilter(item, context),
        } as FilterItem<FilterTypeValue>;
      }
      return filter;
    });
  }, [filters]);

  const renderDomainFilter = (item: FilterItem<FilterTypeValue>, context: FilterRenderContext) => {
    switch (item.type) {
      case AppFilterType.employee:
      case AppFilterType.employeeDivision:
      case AppFilterType.employeeJobProfile:
      case AppFilterType.employeeJobRole:
      case AppFilterType.employeeDepartment:
      case AppFilterType.employeeTeam:
      case AppFilterType.employeeLocation:
      case AppFilterType.employeeLifecycleStatus:
      case AppFilterType.employmentType:
      case AppFilterType.employmentContract:
      case AppFilterType.employmentLegalEntity:
      case AppFilterType.employeeHiredOn:
      case AppFilterType.employeeBirthday:
      case AppFilterType.employeeTerminationDate:
        return (
          <EmployeeDomainFilterRenderer item={item} context={context} filters={enhancedFilters} />
        );
      case AppFilterType.goalOwner:
        return <GoalDomainFilterRenderer item={item} context={context} />;
      case AppFilterType.jobs:
      case AppFilterType.disqualificationReason:
      case AppFilterType.jobCreationDate:
        return (
          <RecruitDomainFilterRenderer item={item} context={context} filters={enhancedFilters} />
        );
      case AppFilterType.formParticipantStatus:
        return (
          <FormParticipantStatusFilterRender
            item={item}
            control={context.control}
            errors={context.errors}
          />
        );
      case AppFilterType.approvalRequestStatus:
        return <ApprovalDomainFilterRenderer item={item} context={context} />;
      case AppFilterType.timeOffCategory:
        return (
          <TimeOffDomainFilterRenderer item={item} context={context} filters={enhancedFilters} />
        );
      default:
        return null;
    }
  };

  return (
    <FilterModal
      filters={enhancedFilters}
      currentFilter={currentFilter}
      source={source}
      onClose={() => close(id)}
      trackFilterEvent={trackFilterEvent}
      DatepickerComponent={Datepicker}
      modalProps={modalProps}
    />
  );
};
